import React from "react";
import { BarChart3 } from "lucide-react";

export default function UsagePanel({ usage }) {
    const payload = usage && typeof usage === "object" ? usage : {};
    const models = Array.isArray(payload.models) ? payload.models : [];
    const paidUsed = Number(payload.paid_used_today ?? payload.used_today ?? 0);
    const paidLimit = Number(payload.paid_daily_limit ?? payload.daily_limit ?? 0);
    const paidPercent = Number(payload.paid_used_percent ?? payload.used_percent ?? 0);
    const vllmUsed = Number(payload.vllm_used_today ?? payload.ollama_used_today ?? 0);
    const fallbackCount = Number(payload.fallback_count_today ?? 0);
    const fmtNumber = (value) => new Intl.NumberFormat("en-US").format(Number(value || 0));
    const rawDate = payload?.date || payload?.last_reset_date || null;
    const thaiDateText = (() => {
        if (!rawDate) return null;
        const d = new Date(`${rawDate}T00:00:00`);
        if (Number.isNaN(d.getTime())) return null;
        return new Intl.DateTimeFormat("th-TH", {
            dateStyle: "full",
            timeZone: "Asia/Bangkok",
        }).format(d);
    })();
    const clampedPercent = Math.min(Math.max(paidPercent, 0), 100);
    const usageStatus = (() => {
        if (paidLimit <= 0) {
            return {
                tone: "neutral",
                title: "กำลังรอข้อมูลโควต้า",
                message: "ระบบกำลังซิงก์ข้อมูลการใช้งาน โปรดลองอีกครั้งในอีกสักครู่",
            };
        }
        if (clampedPercent >= 100) {
            return {
                tone: "critical",
                title: "โควต้าของโมเดลขั้นสูงหมดแล้ววันนี้",
                message:
                    "ตอนนี้ระบบจะพาไปใช้โมเดลฟรีให้อัตโนมัติ เพื่อให้คุณใช้งานได้ต่อเนื่อง",
            };
        }
        if (clampedPercent >= 85) {
            return {
                tone: "warning",
                title: "ใกล้ถึงขีดจำกัดของวันนี้",
                message:
                    "คุณยังใช้งานได้ตามปกติ แต่ระบบอาจสลับไปโมเดลฟรีชั่วคราวเมื่อโควต้าเต็ม",
            };
        }
        return {
            tone: "healthy",
            title: "ใช้งานได้ตามปกติ",
            message: "โควต้ายังเหลือเพียงพอสำหรับโมเดลขั้นสูงในวันนี้",
        };
    })();
    const statusToneClass = {
        healthy: "bg-emerald-50 text-emerald-700 border-emerald-200",
        warning: "bg-amber-50 text-amber-800 border-amber-200",
        critical: "bg-rose-50 text-rose-700 border-rose-200",
        neutral: "bg-slate-50 text-slate-600 border-slate-200",
    }[usageStatus.tone];
    const providerLabel = (provider) =>
        String(provider || "").toLowerCase() === "vllm"
            ? "โมเดลฟรีจากเครื่องประมวลผล AI ของมหาวิทยาลัยราชภัฏพิบูลสงคราม"
            : "โมเดลขั้นสูง";

    return (
        <div className="space-y-4 text-sm">
            {/* 🔹 Header */}
            <h2 className="flex items-center gap-2 font-semibold text-lg text-slate-800">
                <BarChart3 className="text-blue-600" size={18} />
                การใช้งานโมเดลวันนี้
            </h2>
            {thaiDateText && (
                <div className="text-xs text-slate-500 -mt-2">
                    อ้างอิงวันที่: {thaiDateText}
                </div>
            )}

            <div className="bg-white/85 backdrop-blur-md border border-slate-200 rounded-xl p-3">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                    <span>โควต้าโมเดลขั้นสูงจากบริการภายนอก</span>
                    <span>{paidPercent.toFixed(2)}%</span>
                </div>
                <div className="relative h-2.5 w-full bg-slate-100 border border-slate-200 rounded-full overflow-hidden">
                    <div
                        className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300"
                        style={{ width: `${clampedPercent}%` }}
                    />
                </div>
                <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                    <span>ใช้ไปแล้ว {paidPercent.toFixed(2)}%</span>
                </div>
                <div className="mt-1 text-[11px] text-slate-400">
                    ระบบสลับเป็นโมเดลฟรีอัตโนมัติวันนี้: {fmtNumber(fallbackCount)} ครั้ง
                </div>
            </div>
            <div className={`rounded-xl border px-3 py-2 text-xs leading-relaxed ${statusToneClass}`}>
                <div className="font-semibold">{usageStatus.title}</div>
                <div className="mt-0.5">{usageStatus.message}</div>
            </div>

            {/* 🔹 Empty State */}
            {models.length === 0 && (
                <div className="text-slate-500 italic bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg py-2 px-3">
                    วันนี้ยังไม่มีประวัติรายโมเดล แต่อย่างน้อยโมเดลฟรียังใช้งานได้เสมอ
                </div>
            )}

            {/* 🔹 Model Usage */}
            {models.map((m, i) => {
                const percent = Number(m.percent) || 0;
                return (
                    <div
                        key={i}
                        className="mb-3 bg-white/85 backdrop-blur-md border border-slate-200 rounded-xl p-3 hover:shadow-sm transition-all"
                    >
                        <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-slate-700">{m.model}</span>
                            <span className="text-blue-600 font-medium">{percent.toFixed(1)}%</span>
                        </div>

                        {/* ✅ Gradient Progress Bar (หัวมนเท่ากันทุกเปอร์เซ็นต์) */}
                        <div className="relative h-2.5 w-full bg-slate-100 border border-slate-200 rounded-full overflow-hidden backdrop-blur-sm">
                            <div
                                className="absolute left-0 top-0 h-full transition-all duration-500 ease-out
                                           bg-gradient-to-r from-blue-500 to-cyan-500"
                                style={{
                                    width: `${Math.min(percent, 100)}%`,
                                    borderRadius:
                                        percent >= 99
                                            ? "9999px"
                                            : "9999px 6px 6px 9999px", // ✅ หัวมนแบบสมดุล
                                }}
                            ></div>
                        </div>

                        <div className="text-xs text-slate-500 mt-1">
                            ประเภท: <span className="text-slate-700">{providerLabel(m.provider)}</span>
                        </div>
                    </div>
                );
            })}

            {/* 🔹 Info Box */}
            <div className="bg-white/85 backdrop-blur-md border border-slate-200 rounded-xl p-4 shadow-sm text-xs text-slate-600 leading-relaxed">
                หน้านี้ช่วยให้คุณเห็นว่าใช้งานโมเดลขั้นสูงและโมเดลฟรีไปเท่าไรในวันนี้
                <span className="font-medium text-slate-700">
                    {" "}
                    เพื่อวางแผนการใช้งานได้ง่ายขึ้น
                </span>
            </div>
        </div>
    );
}
